import Ajax from '../Ajax'
import { Alert, DisplayDictionary, DisplayValue } from '..'
import { store } from './Store'
import { makeAutoObservable } from 'mobx'
import { CaseNode, CaseSummary } from '../models/Interfaces'
import { format } from 'date-fns'


export class UsageStore {
    
    loading: boolean = true
    caseSummaryTable: CaseSummary[] = []
    tagKeys: string[] = []
    searchString: string;
    accountUid: string;
    orderedBy: string = "createdAt";
    order: string = "desc";

    constructor() {
        makeAutoObservable(this)
    }

    reset = () => {
        this.caseSummaryTable = []
        this.tagKeys = []
        this.searchString = ""
        this.accountUid = ""
        this.orderedBy = "createdAt";
        this.order = "desc";
    }

    refresh = async () => {
        this.loading = true
        var tagKeys = []

        await Ajax.Account.GetCaseSummary(this.accountUid ? this.accountUid : store.AppStore.sessionInfo.userAccount.uid).then((response) => {
            this.caseSummaryTable = response.data
        }).catch(() => {
            Alert({message: "Failed to get Case summary"})
        })

        try {
            this.caseSummaryTable?.forEach((row: CaseSummary) => {
                if (typeof row.tags === "object" && Object.keys(row.tags).length) {
                    Object.keys(row.tags).forEach(key => {
                        if (!this.tagKeys.includes(key)) {
                            tagKeys = [...tagKeys, key]
                        }
                    })
                }
            })

            this.tagKeys = [...new Set(tagKeys)]
        } catch (ex) {
            console.log("Failed to set tagKeys")
        }

        this.loading = false
    }

    get getCaseSummaryTable (): CaseSummary[]  {

        if (!this.caseSummaryTable?.length || !Array.isArray(this.caseSummaryTable)) {
            return []
        }

        // Ordering
        var table = [...this.caseSummaryTable].sort((a, b) => {

            var aValue = null
            var bValue = null

            if (this.tagKeys.includes(this.orderedBy)) {
                aValue = a.tags[this.orderedBy] ? a.tags[this.orderedBy] : ""
                bValue = b.tags[this.orderedBy] ? b.tags[this.orderedBy] : ""
            } else if (Array.isArray(a[this.orderedBy])) {
                aValue = a[this.orderedBy]?.length ? JSON.stringify(a[this.orderedBy]) : ""
                bValue = b[this.orderedBy]?.length ? JSON.stringify(b[this.orderedBy]) : ""
            } else if (typeof a[this.orderedBy] === "object") {
                aValue = a[this.orderedBy]?.name
                bValue = b[this.orderedBy]?.name
            } else if (Object.keys(a).includes(this.orderedBy) && Object.keys(b).includes(this.orderedBy)) {
                aValue = a[this.orderedBy]
                bValue = b[this.orderedBy]
            }

            // Date fields need opposite ordering
            if (typeof a[this.orderedBy] === "number" || ["On", "At"].includes(this.orderedBy.slice(-2))) {
                return this.order === "desc" ? bValue?.localeCompare(aValue) : aValue?.localeCompare(bValue)
            }
            
            return this.order === "desc" ? aValue?.localeCompare(bValue) : bValue?.localeCompare(aValue)
        })

        return table.filter(row => {
            
            if (!this.searchString) return true

            if (JSON.stringify(row)?.toLowerCase().includes(this.searchString?.toLowerCase())) return true

            return false

        })
    }

    get orderPhrase () {
        var orderPhrase = this.order === "asc" ? ", reverse" : ""

        if (["On", "At"].includes(this.orderedBy.slice(-2))) {
            orderPhrase = this.order === "asc" ? ", oldest first" : ", newest first"
        }

        return `ordered by '${DisplayValue("dict_key", this.orderedBy)}'${orderPhrase}`
    }

    setOrder = (order) => {
        this.order = order
    }

    setOrderedBy = (orderedBy) => {
        this.orderedBy = orderedBy
    }

    setSearchString = (str: string) => {
        this.searchString = str
    }

    setAccountUid = (uid: string) => {
        this.accountUid = uid
    }

    downloadTable = async () => {
    
        const formatValue = (value) => {
            if (typeof value === "string") {
                value = ('' + value).replace(/"/g, '\\"')
            } else if (Array.isArray(value)) {
                value = value.map((innerValue) => formatValue(innerValue)).join("\n")
            } else if (typeof value === "object" && value?.name || value?.uid) {
                value = value?.name ? value.name : value.uid
            } else if (value === undefined || value === null || value === "null") {
                value = ""
            } else {
                value = JSON.stringify(value)
            }
            value = value?.replace('"', "'")
            return value
        }

        var csvRows = []
        var headers = [...Object.keys(this.getCaseSummaryTable[0]), ...this.tagKeys]
        csvRows.push(headers.join(','))

        for (const row of this.getCaseSummaryTable) {
            const values = headers.map(header => {
                var value = this.tagKeys.includes(header) && row.tags[header] ? row.tags[header] : row[header]
                // Enclose all cells in "" so that we can format with line breaks
                return `"${formatValue(value)}"`
            })
            csvRows.push(values.join(','))
        }
        const csvString = csvRows.join('\n')

        // 2. Create a Blob
        const blob = new Blob([csvString], { type: 'text/csvcharset=utf-8' })

        // 3. Create a temporary URL
        const url = URL.createObjectURL(blob)

        // 4. Create and trigger a download link
        const link = document.createElement('a')
        link.setAttribute('href', url)
        link.setAttribute('download', `case_summary_${format(new Date(), "yyyy-MM-dd")}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // Clean up the temporary URL
        URL.revokeObjectURL(url)
    }
}